import React from 'react';
import SmartNavbar from '../components/SmartNavbar';
import Footer from '../components/Footer';

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <SmartNavbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Cookie Policy</h1>
        
        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-blue max-w-none">
          <p className="text-sm text-gray-500 mb-6">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. What Are Cookies?</h2>
          <p className="text-gray-700 mb-4">
            Cookies are small text files that are placed on your device when you visit our website. They help us 
            provide you with a better experience by remembering your preferences and understanding how you use our service.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. How We Use Cookies</h2>
          <p className="text-gray-700 mb-4">
            Snaptura uses cookies for the following purposes:
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Essential Cookies (Required)</h3>
          <p className="text-gray-700 mb-4">
            These cookies are necessary for the website to function properly and cannot be disabled:
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            <li className="mb-2"><strong>Authentication:</strong> Keeps you logged in to your account</li>
            <li className="mb-2"><strong>Security:</strong> Protects against unauthorized access and CSRF attacks</li>
            <li className="mb-2"><strong>Session Management:</strong> Maintains your session state across pages</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Functional Cookies (Optional)</h3>
          <p className="text-gray-700 mb-4">
            These cookies enhance functionality and personalization:
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            <li className="mb-2"><strong>Preferences:</strong> Remembers your settings and preferences</li>
            <li className="mb-2"><strong>Language:</strong> Stores your preferred language</li>
            <li className="mb-2"><strong>Theme:</strong> Remembers your display preferences</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Analytics Cookies (Optional)</h3>
          <p className="text-gray-700 mb-4">
            These cookies help us understand how you use our service:
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            <li className="mb-2"><strong>Usage Statistics:</strong> Tracks page views and user interactions</li>
            <li className="mb-2"><strong>Performance Monitoring:</strong> Helps identify and fix issues</li>
            <li className="mb-2"><strong>Feature Analysis:</strong> Understands which features are most valuable</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Cookies We Use</h2>
          <div className="overflow-x-auto mb-6">
            <table className="min-w-full border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Cookie Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Purpose</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Duration</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900">auth_token</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Authentication session</td>
                  <td className="px-4 py-3 text-sm text-gray-700">30 days</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900">csrf_token</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Security protection</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Session</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900">preferences</td>
                  <td className="px-4 py-3 text-sm text-gray-700">User settings</td>
                  <td className="px-4 py-3 text-sm text-gray-700">1 year</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Third-Party Cookies</h2>
          <p className="text-gray-700 mb-4">
            We may use third-party services that set their own cookies:
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            <li className="mb-2"><strong>Stripe:</strong> For payment processing (essential)</li>
            <li className="mb-2"><strong>Analytics Services:</strong> For usage statistics (optional)</li>
          </ul>
          <p className="text-gray-700 mb-4">
            These third parties have their own privacy policies governing their use of cookies.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Managing Cookies</h2>
          <p className="text-gray-700 mb-4">
            You can control and manage cookies in several ways:
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Browser Settings</h3>
          <p className="text-gray-700 mb-4">
            Most browsers allow you to:
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            <li className="mb-2">View and delete cookies</li>
            <li className="mb-2">Block all cookies</li>
            <li className="mb-2">Block third-party cookies only</li>
            <li className="mb-2">Clear cookies when you close your browser</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Browser-Specific Instructions</h3>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            <li className="mb-2"><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
            <li className="mb-2"><strong>Firefox:</strong> Settings → Privacy & Security → Cookies</li>
            <li className="mb-2"><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
            <li className="mb-2"><strong>Edge:</strong> Settings → Cookies and site permissions</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Impact of Disabling Cookies</h2>
          <p className="text-gray-700 mb-4">
            Please note that disabling cookies may affect your experience:
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            <li className="mb-2">You may not be able to stay logged in</li>
            <li className="mb-2">Your preferences won't be saved</li>
            <li className="mb-2">Some features may not work properly</li>
            <li className="mb-2">You'll need to log in every time you visit</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Updates to This Policy</h2>
          <p className="text-gray-700 mb-4">
            We may update this Cookie Policy from time to time to reflect changes in our practices or for legal reasons. 
            We will notify you of any significant changes by posting the new policy on this page.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Contact Us</h2>
          <p className="text-gray-700 mb-4">
            If you have questions about our use of cookies, please contact us at:
          </p>
          <p className="text-gray-700">
            Email: <a href="mailto:privacy@snaptura.online" className="text-blue-600 hover:underline">privacy@snaptura.online</a>
          </p>

          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> By continuing to use Snaptura, you consent to our use of cookies as described in this policy.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}